/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { Button } from 'reactstrap';
import React, { useState } from 'react';
import { SurveyModelMinData } from '../../../types/survey';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { useToggleCompleteSurveys } from '../../../hooks/useToggleCompleteSurveys';
import { CompleteSurveyModal } from '../complete-survey-modal';
import './styles.scss';
import { SURVEY } from '@constants/terminology';
import { AppCode } from '@g17eco/types/app';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';

interface Props {
  isUserAdmin: boolean;
  survey: Pick<SurveyModelMinData, 'completedDate' | '_id' | 'initiativeId'>;
  handleReload: () => Promise<any>;
  classes?: { btn?: string; icon?: string };
  disabled?: boolean;
  color?: 'link' | 'secondary' | 'primary' | 'success';
  completedText?: string;
  notCompletedText?: string;
  notCompletedIcon?: string;
  showPopup?: boolean;
  appConfigCode?: AppCode;
  tooltip?: string;
}

const getCompleteSurveyTooltip = (appConfigCode?: AppCode) => {
  return appConfigCode === AppCode.SGXESGenome
    ? `By clicking this button, I confirm that content within this ${SURVEY.SINGULAR} is accurate, based on factual data, and ready for disclosure.`
    : `This will lock your ${SURVEY.SINGULAR} to stop new data being added. You can unlock anytime.`;
};

export const CompleteButton = (props: Props) => {
  const {
    isUserAdmin,
    survey,
    handleReload,
    classes = { btn: 'ml-2 complete-btn', icon: '' },
    color,
    disabled = false,
    showPopup = true,
    appConfigCode,
    tooltip,
  } = props;

  const isComplete = Boolean(survey.completedDate);
  const { isSaving, toggleCompleteSurveys } = useToggleCompleteSurveys({
    handleReload,
    surveys: [survey],
    isCompleting: !isComplete,
  });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const toggle = () => setIsOpen(!isOpen);

  const onClickComplete = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (showPopup) {
      return toggle();
    }
    return toggleCompleteSurveys();
  };


  if (!isUserAdmin) {
    if (isComplete) {
      return <span className='mr-3 text-ThemeTextPlaceholder'><i className='fa-light fa-lock-keyhole' />{SURVEY.CAPITALIZED_SINGULAR} completed</span>;
    }
    return null;
  }

  const completedTooltip =
    tooltip ?? (isComplete ? `Click to unlock the ${SURVEY.SINGULAR}` : getCompleteSurveyTooltip(appConfigCode));
  const completedText = props.completedText ?? `${SURVEY.CAPITALIZED_SINGULAR} completed`;
  const notCompletedText = props.notCompletedText ?? 'Mark as complete';
  const completedIcon = 'fa-lock-keyhole';
  const notCompletedIcon = props.notCompletedIcon ?? 'fa-lock-keyhole-open';
  const hasIcon = Boolean(classes.icon || (isComplete ? completedIcon : notCompletedIcon));

  return (
    <>
      {isSaving ? <BlockingLoader /> : null}
      <SimpleTooltip text={completedTooltip}>
        <Button
          color={color ?? (isComplete ? 'success' : 'secondary')}
          disabled={disabled || !isUserAdmin}
          defaultChecked={isComplete}
          onClick={isComplete ? toggleCompleteSurveys : onClickComplete}
          className={`d-flex justify-content-center align-items-center ${classes.btn}`}
          data-testid='survey-complete-btn'
        >
          {hasIcon ? <i className={`${classes.icon} fa-light ${isComplete ? completedIcon : notCompletedIcon} me-2`} /> : null}
          {isComplete ? completedText : notCompletedText}
        </Button>
      </SimpleTooltip>
      <CompleteSurveyModal
        isOpen={isOpen}
        toggle={toggle}
        completeSurveyCallback={toggleCompleteSurveys}
      />
    </>
  );
}
